import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './FaqPage.css';

const FaqPage = () => {
  const [openFaqs, setOpenFaqs] = useState({});

  const toggleFaq = (id) => {
    setOpenFaqs(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const faqCategories = [
    {
      id: 'general',
      title: 'General Questions',
      faqs: [
        {
          id: 'what-is-cataract',
          question: 'What is a cataract?',
          answer: 'A cataract is a clouding of the natural lens in your eye. This clouding affects how light passes through your eye and can cause blurry vision, glare sensitivity, and difficulty seeing at night. Cataracts typically develop slowly over time as part of the aging process, though they can also result from injury, certain medications, or medical conditions.'
        },
        {
          id: 'cataract-surgery',
          question: 'What happens during cataract surgery?',
          answer: 'During cataract surgery, the clouded natural lens is removed and replaced with an artificial intraocular lens (IOL). The procedure is typically performed on an outpatient basis and takes about 15-30 minutes per eye. The surgeon makes a tiny incision in the eye, breaks up the cloudy lens using ultrasound (phacoemulsification), removes it, and then inserts the new artificial lens. Most people remain awake during the procedure but receive local anesthesia to numb the eye and may be given a mild sedative to help them relax.'
        },
        {
          id: 'how-site-works',
          question: 'How does CataractLensAdvisor work?',
          answer: 'CataractLensAdvisor uses a comprehensive questionnaire to gather information about your vision needs, lifestyle, budget, and medical factors. Our recommendation engine then analyzes this information against our database of cataract lens options to provide personalized recommendations. The database is regularly updated using the Perplexity AI research tool to ensure the information is current and accurate. We provide unbiased recommendations based solely on your needs, not influenced by manufacturers or sponsors.'
        },
        {
          id: 'medical-advice',
          question: 'Is this medical advice?',
          answer: 'No, CataractLensAdvisor provides informational content only and is not a substitute for professional medical advice. The recommendations are meant to help you have more informed discussions with your ophthalmologist. Always consult with qualified healthcare providers for medical decisions regarding your eye health and cataract treatment options.'
        }
      ]
    },
    {
      id: 'lens-types',
      title: 'Lens Types and Options',
      faqs: [
        {
          id: 'lens-types',
          question: 'What are the different types of cataract lenses available?',
          answer: 'There are several types of intraocular lenses (IOLs) available for cataract surgery: 1) Monofocal lenses provide clear vision at one distance (usually far) and are typically covered by insurance; 2) Multifocal lenses provide vision at multiple distances but may cause more visual disturbances; 3) Extended Depth of Focus (EDOF) lenses provide continuous vision from distance to intermediate with fewer visual disturbances; 4) Toric lenses correct astigmatism; and 5) Accommodating lenses attempt to mimic the eye\'s natural focusing ability. Each type has its own benefits and considerations based on your specific needs.'
        },
        {
          id: 'best-lens',
          question: 'Which lens is the best?',
          answer: 'There is no single "best" lens for everyone. The ideal lens depends on your specific vision needs, lifestyle, budget, and medical factors. For example, if you drive frequently at night, a monofocal or EDOF lens might be better due to fewer visual disturbances. If you want to reduce dependence on reading glasses, a multifocal lens might be more suitable. Our questionnaire helps identify which lens options might be most appropriate for your individual situation.'
        },
        {
          id: 'insurance-coverage',
          question: 'Which lenses are covered by insurance?',
          answer: 'Most insurance plans, including Medicare, cover standard monofocal lenses for cataract surgery. Premium lenses such as multifocal, EDOF, toric, and accommodating lenses typically require additional out-of-pocket payment. The amount varies by lens type and provider. For example, toric lenses for astigmatism correction might add $1,000-$1,500 per eye, while multifocal lenses might add $2,000-$3,000 per eye. Check with your insurance provider and surgeon\'s office for specific coverage details.'
        },
        {
          id: 'lens-lifespan',
          question: 'How long do cataract lenses last?',
          answer: 'Intraocular lenses (IOLs) are designed to last a lifetime. They are made from durable materials that do not degrade over time. Once implanted, they become a permanent part of your eye and do not need to be replaced under normal circumstances. However, some patients may develop a condition called posterior capsule opacification (PCO), sometimes referred to as a "secondary cataract," where the capsule that holds the lens becomes cloudy. This can be easily treated with a quick laser procedure called YAG laser capsulotomy.'
        }
      ]
    },
    {
      id: 'procedure',
      title: 'Procedure and Recovery',
      faqs: [
        {
          id: 'procedure-painful',
          question: 'Is cataract surgery painful?',
          answer: 'Cataract surgery is generally not painful. Your eye will be numbed with local anesthetic drops or injections before the procedure. You may feel some pressure or mild discomfort, but not pain. After surgery, some patients experience mild discomfort, itching, or a feeling that something is in the eye, but severe pain is rare and should be reported to your doctor immediately as it could indicate a complication.'
        },
        {
          id: 'recovery-time',
          question: 'How long is the recovery period?',
          answer: 'Most people notice improved vision within a few days after cataract surgery, though complete healing typically takes 4-6 weeks. Initially, your vision may be blurry as your eye heals and adjusts to the new lens. You\'ll need to use prescribed eye drops, avoid rubbing your eye, wear an eye shield while sleeping, and avoid strenuous activities for a week or so. You\'ll have follow-up appointments to monitor your healing. Most people can resume normal activities, including reading and watching TV, within a day or two, though you should avoid swimming and heavy lifting for a few weeks.'
        },
        {
          id: 'both-eyes',
          question: 'Do both eyes need surgery at the same time?',
          answer: 'No, cataract surgery is typically performed on one eye at a time, with the second eye done a few weeks later. This approach allows the first eye to heal and vision to stabilize before operating on the second eye. It also gives you and your surgeon the opportunity to evaluate the results of the first surgery and make any necessary adjustments for the second eye. In rare cases, both eyes might be done on the same day (bilateral simultaneous cataract surgery), but this is not common practice in the United States.'
        },
        {
          id: 'driving-after',
          question: 'When can I drive after cataract surgery?',
          answer: 'Most people can resume driving 24-48 hours after cataract surgery, assuming your vision meets the legal requirements for driving in your state and you feel comfortable. However, this varies by individual and depends on factors such as how quickly your vision clears, whether you have surgery on both eyes, and your surgeon\'s specific recommendations. You will need someone to drive you home after the procedure, as you cannot drive immediately following surgery. Always follow your doctor\'s advice about when it\'s safe to resume driving.'
        }
      ]
    },
    {
      id: 'lifestyle',
      title: 'Lifestyle and Vision Expectations',
      faqs: [
        {
          id: 'glasses-after',
          question: 'Will I still need glasses after cataract surgery?',
          answer: 'Whether you\'ll need glasses after cataract surgery depends on the type of intraocular lens (IOL) you choose. With standard monofocal lenses, you\'ll likely need glasses for certain activities—typically reading glasses if your lenses are set for distance vision. Premium lenses like multifocal or EDOF lenses can reduce dependence on glasses, but many people still find they need glasses for certain tasks or in challenging lighting conditions. Complete freedom from glasses cannot be guaranteed with any lens type, though many patients achieve high levels of glasses independence with premium lenses.'
        },
        {
          id: 'night-vision',
          question: 'How will cataract surgery affect my night vision?',
          answer: 'Cataract surgery often improves night vision by removing the cloudy lens that scatters light and causes glare. However, the type of replacement lens can affect night vision quality. Monofocal lenses typically provide good night vision with minimal visual disturbances. Multifocal lenses may cause more halos or glare around lights at night, which can be bothersome for some patients, particularly those who drive frequently at night. EDOF lenses generally cause fewer visual disturbances than multifocal lenses but more than monofocal lenses. Most patients adapt to these visual phenomena over time.'
        },
        {
          id: 'reading-computer',
          question: 'Which lens is best for reading and computer work?',
          answer: 'For reading and computer work, several options exist: 1) Multifocal lenses provide vision at multiple distances, including reading distance; 2) EDOF (Extended Depth of Focus) lenses work well for intermediate distances like computer screens but may require reading glasses for small print; 3) Monovision, where one eye is corrected for distance and one for near, can work well for some patients; 4) Accommodating lenses attempt to provide some focusing ability. The best choice depends on your specific needs, such as how much time you spend reading versus on a computer, the importance of reducing glasses dependence, and your tolerance for potential visual side effects.'
        },
        {
          id: 'outdoor-activities',
          question: 'Which lenses are best for outdoor and sports activities?',
          answer: 'For outdoor and sports activities, consider these factors: 1) If you need sharp distance vision (golf, tennis, hiking), monofocal lenses set for distance or EDOF lenses often work well; 2) If you need to see both far and near without glasses (checking a golf scorecard, reading a map while hiking), multifocal or EDOF lenses might be better; 3) For water sports where glasses aren\'t practical, reducing glasses dependence with premium lenses may be valuable; 4) If you have astigmatism and participate in precision sports, toric lenses can provide clearer vision. Discuss your specific activities with your doctor to find the best match.'
        }
      ]
    },
    {
      id: 'site-usage',
      title: 'Using CataractLensAdvisor',
      faqs: [
        {
          id: 'data-privacy',
          question: 'How is my data used and protected?',
          answer: 'CataractLensAdvisor takes your privacy seriously. The information you provide in the questionnaire is used solely to generate personalized lens recommendations. We do not share your personal information with third parties, including lens manufacturers or healthcare providers. Your data is stored securely and is not used for marketing purposes without your explicit consent. For complete details, please review our Privacy Policy accessible from the footer of our website.'
        },
        {
          id: 'premium-report',
          question: 'What\'s included in the premium report?',
          answer: 'The premium report ($5) includes: 1) Detailed analysis of each recommended lens with personalized explanations of why they match your needs; 2) Comprehensive comparison charts showing how different lenses perform across various activities and conditions specific to your lifestyle; 3) Educational content about cataract surgery and recovery; 4) A customized list of questions to ask your ophthalmologist based on your questionnaire responses; 5) Visual simulations of how different lens options might affect your vision; and 6) The option to receive the report via email. The report is delivered as a downloadable PDF that you can print and take to your doctor\'s appointment.'
        },
        {
          id: 'recommendation-accuracy',
          question: 'How accurate are the recommendations?',
          answer: 'Our recommendations are based on comprehensive research and data about available cataract lenses, matched against your specific needs and preferences. While we strive for accuracy, these recommendations should be considered informational rather than definitive. The final decision should always be made in consultation with your ophthalmologist, who can consider additional factors such as your eye measurements, surgical considerations, and specific health conditions. Think of our recommendations as a starting point for an informed discussion with your doctor.'
        },
        {
          id: 'update-frequency',
          question: 'How often is the lens database updated?',
          answer: 'Our lens database is automatically checked for updates monthly using the Perplexity AI research tool. If the database is older than one month, it is refreshed with the latest information about available lenses, including new products, updated pricing, and recent clinical data. This ensures that our recommendations reflect the most current options available in the market. The last update date is displayed on the results page so you can see how recent the information is.'
        }
      ]
    }
  ];

  return (
    <div className="faq-page">
      <div className="container">
        <h1 className="section-title">Frequently Asked Questions</h1>
        <p className="section-subtitle">Find answers to common questions about cataracts, lens options, and our service</p>
        
        <div className="faq-search-container">
          <div className="faq-search">
            <input 
              type="text" 
              placeholder="Search questions..." 
              className="search-input"
            />
            <button className="search-button">
              Search
            </button>
          </div>
        </div>
        
        <div className="faq-categories">
          {faqCategories.map(category => (
            <div className="faq-category" key={category.id}>
              <h2 className="category-title">{category.title}</h2>
              
              <div className="faq-list">
                {category.faqs.map(faq => (
                  <div 
                    className={`faq-item ${openFaqs[faq.id] ? 'open' : ''}`} 
                    key={faq.id}
                  >
                    <div 
                      className="faq-question"
                      onClick={() => toggleFaq(faq.id)}
                    >
                      <h3>{faq.question}</h3>
                      <span className="faq-toggle">
                        {openFaqs[faq.id] ? '−' : '+'}
                      </span>
                    </div>
                    
                    {openFaqs[faq.id] && (
                      <div className="faq-answer">
                        <p>{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        
        <div className="still-have-questions">
          <h2>Still Have Questions?</h2>
          <p>
            Our questionnaire can help you find personalized lens recommendations based on your 
            specific needs and preferences.
          </p>
          <div className="cta-container">
            <Link to="/questionnaire" className="btn btn-primary">
              Start Lens Assessment
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FaqPage;
